import React, { useEffect } from "react";
import Pagination from "../../../components/Pagination";
import { CategoryCard, CategoryEditModal } from "./components";
import { useCategoryManagement } from "./hooks";
import "react-toastify/dist/ReactToastify.css";

/**
 * CategoryList Component
 * Main component for displaying and managing categories
 */
const CategoryList = ({ searchTerm = "" }) => {
  const {
    loading,
    openModal,
    setOpenModal,
    selectedCategory,
    newImage,
    setNewImage,
    currentPage,
    setCurrentPage,
    editOtherLang,
    setEditOtherLang,
    languages,
    selectedLang,
    setSelectedLang,
    translations,
    setTranslations,
    printerCategories,
    ENABLE_PRINT_CAT,
    printKey,
    setPrintKey,
    isVisible,
    setIsVisible,
    handleCategorySelect,
    handleUpdateCategory,
    getFilteredCategories,
    getPaginatedCategories,
  } = useCategoryManagement();

  const filteredCategories = getFilteredCategories(searchTerm);
  const paginatedCategories = getPaginatedCategories(
    filteredCategories,
    currentPage,
  );
  const filteredTotalPages = Math.ceil(filteredCategories.length / 40);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, setCurrentPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Categories Grid */}
      {filteredCategories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center mb-6">
            <svg
              className="w-12 h-12 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {searchTerm ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              )}
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {searchTerm ? "No results found" : "No categories yet"}
          </h3>
          <p className="text-sm text-gray-600 text-center max-w-sm mb-6">
            {searchTerm
              ? "We couldn't find any categories matching your search. Try adjusting your search terms."
              : "Get started by creating your first category to organize your products."}
          </p>
          {!searchTerm && (
            <button className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Category
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {paginatedCategories.map((category) => (
              <CategoryCard
                key={category._id}
                category={category}
                onClick={handleCategorySelect}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={filteredTotalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </>
      )}

      {/* Edit Modal */}
      <CategoryEditModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        category={selectedCategory}
        onSubmit={handleUpdateCategory}
        editOtherLang={editOtherLang}
        setEditOtherLang={setEditOtherLang}
        languages={languages}
        selectedLang={selectedLang}
        setSelectedLang={setSelectedLang}
        translations={translations}
        setTranslations={setTranslations}
        newImage={newImage}
        setNewImage={setNewImage}
        printerCategories={printerCategories}
        enablePrintCat={ENABLE_PRINT_CAT}
        printKey={printKey}
        setPrintKey={setPrintKey}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
      />
    </div>
  );
};

export default CategoryList;
